import useSWR, { mutate as mutateGlobal } from 'swr';
import axios from 'axios';
import * as React from 'react';
import { ApiKeyContext } from 'contexts/ApiKeyContext';
import config from 'config';
import { Itinerary } from 'models/itinerary';
import { useVisitorIdentifier } from 'hooks/useVisitorIdentifier';
import { useCustomerToken } from 'hooks/useCustomerToken';
import { getCommonRedirectUri } from 'lib/util/customer';
import { useSelector } from 'react-redux';
import { selectDigitalMap } from 'ducks/universal/digitalMap';
import { useUrlQueryDisplayState } from './useUrlQueryDisplayState';

interface ItineraryResponse {
  guest_itineraries: Itinerary[];
}

const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';

const debugLog = (...args: any[]) => {
  const shouldLog = false;
  if (isLocalhost && shouldLog) {
    console.log('[useItineraries]', ...args);
  }
};

const fetcher = (
  url: string,
  apiKey: string,
  trackeeId: string,
  accessToken: string,
  idProvider: string,
  redirectUri: string
) => {
  debugLog('Fetching itineraries with params:', {
    url,
    trackeeId,
    idProvider,
    hasAccessToken: !!accessToken,
  });

  return axios
    .get(url, {
      headers: {
        'x-api-key': apiKey,
        'x-track-id': trackeeId,
        'x-customer-access-token': accessToken,
        'x-customer-id-provider': idProvider,
        'x-customer-redirect-uri': redirectUri,
      },
    })
    .then((response) => {
      debugLog('Fetched itineraries:', response.data);
      return response.data;
    });
};

export const useItineraries = () => {
  const { apiKey } = React.useContext(ApiKeyContext);
  const { trackingId } = useVisitorIdentifier();
  const { accessToken, idProvider } = useCustomerToken();
  const digitalMap = useSelector(selectDigitalMap);
  const redirectUri = typeof window !== 'undefined' ? getCommonRedirectUri() : '';
  const { selectedItineraryId } = useUrlQueryDisplayState();

  // Create cache keys for both endpoints
  const listCacheKey = React.useMemo(
    () => [
      `${config.apiUrl}/guestitineraries`,
      apiKey,
      trackingId,
      accessToken,
      idProvider,
      redirectUri,
    ],
    [apiKey, trackingId, accessToken, idProvider, redirectUri]
  );

  const detailsCacheKey = React.useMemo(
    () =>
      selectedItineraryId
        ? [
            `${config.apiUrl}/guestitineraries/${selectedItineraryId}`,
            apiKey,
            trackingId,
            accessToken,
            idProvider,
            redirectUri,
          ]
        : null,
    [selectedItineraryId, apiKey, trackingId, accessToken, idProvider, redirectUri]
  );

  const { data, error } = useSWR<ItineraryResponse>(listCacheKey, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false,
    dedupingInterval: 5000,
  });

  const {
    data: selectedItinerary,
    error: selectedItineraryError,
    mutate: mutateSelectedItinerary,
  } = useSWR<Itinerary>(detailsCacheKey, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false,
    dedupingInterval: 5000,
  });

  // Function to mutate both list and details cache
  const mutateAll = React.useCallback(
    async (
      mutateListFn?: (prevData: ItineraryResponse | undefined) => ItineraryResponse,
      revalidate = false
    ) => {
      // Mutate the list cache
      await mutateGlobal(listCacheKey, mutateListFn, { revalidate });
    },
    [listCacheKey]
  );

  React.useEffect(() => {
    if (error) {
      debugLog('Error fetching itineraries:', error);
    }
  }, [error]);

  React.useEffect(() => {
    debugLog('Itineraries data changed:', data?.guest_itineraries);
  }, [data]);

  React.useEffect(() => {
    debugLog('Selected itinerary changed:', selectedItinerary);
  }, [selectedItinerary]);

  const createItinerary = React.useCallback(
    async (itinerary: Omit<Itinerary, 'id'>) => {
      debugLog('Creating itinerary:', itinerary);
      try {
        const response = await axios.post(`${config.apiUrl}/guestitineraries`, itinerary, {
          headers: {
            'x-api-key': apiKey,
            'x-track-id': trackingId,
            'x-customer-access-token': accessToken,
            'x-customer-id-provider': idProvider,
            'x-customer-redirect-uri': redirectUri,
          },
        });
        debugLog('Created itinerary response:', response.data);
        await mutateAll(
          (prevData) => ({
            ...prevData,
            guest_itineraries: [...(prevData?.guest_itineraries ?? []), response.data],
          }),
          false
        );
        return response.data;
      } catch (error) {
        debugLog('Error creating itinerary:', error);
        throw error;
      }
    },
    [apiKey, trackingId, accessToken, idProvider, redirectUri, mutateAll]
  );

  const updateItinerary = React.useCallback(
    async (id: string, updates: Partial<Itinerary>) => {
      debugLog('Updating itinerary:', { id, updates });
      try {
        const response = await axios.patch(`${config.apiUrl}/guestitineraries/${id}`, updates, {
          headers: {
            'x-api-key': apiKey,
            'x-track-id': trackingId,
            'x-customer-access-token': accessToken,
            'x-customer-id-provider': idProvider,
            'x-customer-redirect-uri': redirectUri,
          },
        });
        debugLog('Updated itinerary response:', response.data);
        await mutateAll(
          (prevData) => ({
            ...prevData,
            guest_itineraries:
              prevData?.guest_itineraries?.map((itinerary) =>
                itinerary.id === id ? { ...itinerary, ...updates } : itinerary
              ) ?? [],
          }),
          false
        );
        mutateSelectedItinerary(
          (prevData) =>
            selectedItineraryId === id && prevData
              ? { ...prevData, ...updates, id, digital_map_id: prevData.digital_map_id }
              : prevData,
          { revalidate: false }
        );
        return response.data;
      } catch (error) {
        debugLog('Error updating itinerary:', error);
        throw error;
      }
    },
    [apiKey, trackingId, accessToken, idProvider, redirectUri, mutateAll]
  );

  const deleteItinerary = React.useCallback(
    async (id: string) => {
      debugLog('Deleting itinerary:', id);
      try {
        await axios.delete(`${config.apiUrl}/guestitineraries/${id}`, {
          headers: {
            'x-api-key': apiKey,
            'x-track-id': trackingId,
            'x-customer-access-token': accessToken,
            'x-customer-id-provider': idProvider,
            'x-customer-redirect-uri': redirectUri,
          },
        });
        debugLog('Deleted itinerary:', id);
        await mutateAll(
          (prevData) => ({
            ...prevData,
            guest_itineraries:
              prevData?.guest_itineraries?.filter((itinerary) => itinerary.id !== id) ?? [],
          }),
          false
        );
      } catch (error) {
        debugLog('Error deleting itinerary:', error);
        throw error;
      }
    },
    [apiKey, trackingId, accessToken, idProvider, redirectUri, mutateAll]
  );

  const rebaseItineraries = React.useCallback(
    async (digitalMapId: string) => {
      if (!accessToken || !idProvider || !trackingId || !digitalMapId) return;

      try {
        await axios.post(
          `${config.apiUrl}/guestitineraries/rebase`,
          {
            digital_map_id: digitalMapId,
          },
          {
            headers: {
              'x-api-key': apiKey,
              'x-track-id': trackingId,
              'x-customer-access-token': accessToken,
              'x-customer-id-provider': idProvider,
              'x-customer-redirect-uri': getCommonRedirectUri(),
            },
          }
        );

        // Revalidate after 1 second
        setTimeout(() => {
          mutateAll(undefined, true);
        }, 1000);
      } catch (error) {
        debugLog('Error rebasing itineraries:', error);
        throw error;
      }
    },
    [apiKey, trackingId, accessToken, idProvider, mutateAll]
  );

  return {
    itineraries:
      data?.guest_itineraries?.filter((itinerary) => itinerary.digital_map_id === digitalMap?.id) ??
      [],
    selectedItinerary:
      selectedItinerary ??
      (selectedItineraryId
        ? data?.guest_itineraries.find((itinerary) => itinerary.id === selectedItineraryId)
        : null),
    isLoading: !error && !data,
    isError: error || selectedItineraryError,
    createItinerary,
    updateItinerary,
    deleteItinerary,
    rebaseItineraries,
    mutateAll,
  };
};
